/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power2, Power3, Timeline, TimelineLite, TweenLite} from "gsap";
import {BarbarianFuryGameAssets} from "../../BarbarianFuryGameAssets";
import {BarbarianFuryGameConfig} from "../../BarbarianFuryGameConfig";
import {BarbarianFurySoundConfig} from '../../BarbarianFurySoundConfig';

export class BarbarianFuryInitialWinCountUp extends BitmapWinCountUp {

    private _isCountUpSoundPlaying:boolean = false;
    private _hasCountUpEndSoundPlayed:boolean = false;
    private _hasMultiplierEndSoundPlayed:boolean = false;
    private _isShowing:boolean = false;
    private _endValue:number;
    private _endMultiplier:number;
    private _winAmountShadow:PIXI.Sprite;

    constructor() {
        super({
            font : {
                name : "NumbersWinCountUp",
                size : 27
            },
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
    }

    protected initAnimations():void {
        super.initAnimations();
        this._winAmountShadow = this._winAmountShadow = new PIXI.Sprite(GameResources.getTextures(BarbarianFuryGameAssets.WIN_AMOUNT_SHADOW)[0]);
        this._winAmountShadow.position.set(BarbarianFuryGameConfig.instance.GAME_WIDTH * 0.5, BarbarianFuryGameConfig.instance.GAME_HEIGHT * 0.5);
        this._winAmountShadow.anchor.set(0.55, 0.5);
        this._winAmountShadow.alpha = 0.82;
        this._winAmountShadow.scale.x = 0.8 * 2.6;
        this._winAmountShadow.visible = false;
        StageManager.getLayer(BarbarianFuryGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChildAt(this._winAmountShadow, 0);
    }

    public getCountUpAnimation(from:number, to:number, duration:number, multiplier:number):Timeline {
        this._winAmountShadow.visible = true;
        this._endValue = to;
        this._endMultiplier = multiplier;
        const oriNumber:number = to / multiplier;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this.setMaxTextWidth(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
                if(duration > 0.01) {
                    SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.COUNT_UP);
                    this._isCountUpSoundPlaying = true;
                }
                this._isShowing = true;
            },
            this._countUp.getCountUpAnimation(from, oriNumber, {duration : duration, ease : Power3.easeInOut}),
        ]);

        timeline.add(() => {
            this.stopCountUpSound();
        });

        if(multiplier > 1) {
            const levelTime:number = 0.5 * multiplier > 5 ? MathHelper.roundToDecimals(5 / multiplier, 2) : 0.5;
            for(let m:number = 1; m <= multiplier; m++) {
                const showNumber:number = oriNumber * m;
                const scale:number = this.getScale(m, multiplier);
                let mIndex:number = (m % 2 === 0) ? (m % 2) + 2 : m / m;
                if(m > 1) {
                    if(m === multiplier) {
                        this._hasMultiplierEndSoundPlayed = true;
                        if (!BarbarianFuryGameConfig.instance.WIN_BELOW_STAKE) {
                            timeline.add(() => {
                                SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.MULTIPLIER_COUNT_UP_ENDS);
                            });
                        }
                    } else {
                        if (!BarbarianFuryGameConfig.instance.WIN_BELOW_STAKE) {
                            timeline.add(() => {
                                SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.MULTIPLIER_COUNT_UP + mIndex);
                            });
                        }
                    }
                }
                timeline.add([
                    this._countUp.getCountUpAnimation(showNumber, showNumber, {duration : 0.01}),
                    TweenLite.to(this._countUpContainer.scale, levelTime / 2,
                        {
                            x : scale,
                            y : scale,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                    TweenLite.to(this._winAmountShadow.scale, levelTime / 2,
                        {
                            x : scale * 0.8 * 2.6,
                            y : scale * 2.3,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                ]);
                timeline.add(TweenLite.to(this, levelTime / 2, {}));
            }
        }

        return timeline;
    }

    public abort(endValue:number, isAborted:boolean, multiplier:number, isBigWin:boolean):Timeline | null {
        if(isBigWin) {
            return null;
        }

        this._endValue = endValue;
        this._endMultiplier = multiplier;
        return super.abort(endValue, isAborted, multiplier);
    }

    public getEndingAnimation(endValue:number):Timeline {
        const timeline:TimelineLite = new TimelineLite({
            onComplete : () => {
                this._isCountUpSoundPlaying = false;
                this._hasCountUpEndSoundPlayed = false;
                this._hasMultiplierEndSoundPlayed = false;
                this._winAmountShadow.visible = false;
                this._winAmountShadow.scale.set(2.6, 2.3);
                this._winAmountShadow.alpha = 0.82;
            }
        });
        timeline.add(() => {
            this._isShowing = false;
            if(this._endMultiplier > 1 && !this._hasMultiplierEndSoundPlayed) {
                this._hasMultiplierEndSoundPlayed = true;
                this._hasCountUpEndSoundPlayed = true;
            }
            this.stopCountUpSound();
        });
        timeline.add(this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01}));
        timeline.add([
            TweenLite.to(this._countUp, 0.5, {alpha : 0, ease : Power2.easeIn}),
            TweenLite.to(this._countUpContainer, 0.5, {y : -100, ease : Power2.easeIn}),
            TweenLite.to(this._winAmountShadow, 0.3, {alpha : 0, ease : Power2.easeIn}),
        ], `+=0.2`); // stay a while after count up.
        timeline.add(() => this._countUpContainer.y = 0);
        return timeline;
    }

    protected getAbortAnimation(endValue:number, multiplier:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this.setMaxTextWidth(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._winAmountShadow.visible = false;
                this._winAmountShadow.alpha = 0.82;
                this._winAmountShadow.scale.set(2.6, 2.3);
                this._countUpContainer.scale.set(1, 1);
                this._isShowing = true;
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01})
        ]);
        if(multiplier > 1) {
            if (!BarbarianFuryGameConfig.instance.WIN_BELOW_STAKE) {
                SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.MULTIPLIER_COUNT_UP_ENDS);
            }
            const scale:number = this.getScale(multiplier, multiplier);
            timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                x : scale,
                y : scale,
                ease : Elastic.easeOut.config(2, 1)
            }), 0);
        }

        return timeline
    }

    private getScale(multiplier:number, finalMultiplier:number):number {
        const maxScale:number = 1.5,
            minScale:number = 1;

        let retVal:number = 1 + ((multiplier - 1) * 0.1);

        if((1 + ((finalMultiplier - 1) * 0.1)) > maxScale) {
            retVal = 1 + (((maxScale - minScale) / finalMultiplier) * (multiplier - 1));
        }

        return retVal;
    }

    private stopCountUpSound():void {
        if(this._isCountUpSoundPlaying) {
            SlotGame.sound.stopEffect(BarbarianFurySoundConfig.instance.COUNT_UP);
            this._isCountUpSoundPlaying = false;
        }

        if(!this._hasCountUpEndSoundPlayed) {
            if (!BarbarianFuryGameConfig.instance.WIN_BELOW_STAKE) {
                SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.COUNT_UP_ENDS);
            }
            this._hasCountUpEndSoundPlayed = true;
        }
    }

    // #209
    private setMaxTextWidth(multiplier:number):void {
        let val:number;

        if(multiplier === 1) {
            val = 0.9;
        } else {
            val = 0.6;
        }

        this._countUp.maxTextWidth = BarbarianFuryGameConfig.instance.REEL_AREA_WIDTH * val;
    }
}